import { doTrack } from '@/track/track.service';

const INSIGHT_EVENT = 'Insight';

export const INSIGHT_TELEMETRY_AI_INSIGHT = 'AI Insight';
export const INSIGHT_TELEMETRY_BOARD = 'Board';
export const INSIGHT_TELEMETRY_DRILLING = 'Drilling';
export const INSIGHT_TELEMETRY_EDITING = 'Editing';
export const INSIGHT_TELEMETRY_FILTERING = 'Filtering';
export const INSIGHT_TELEMETRY_TILE = 'Tile';

const INSIGHT_TILE_TYPES = ['AI_SUMMARY', 'CHART', 'KPI', 'TABLE'] as const;
const INSIGHT_CHART_TYPES = [
  'area',
  'bar',
  'bubble',
  'column',
  'donut',
  'heatmap',
  'line',
  'map-line',
  'map-line-background',
  'map-marker',
  'map-marker-series',
  'map-shape',
  'map-shape-background',
  'pie',
  'radial-gauge',
  'scatter',
] as const;
type InsightChartType = (typeof INSIGHT_CHART_TYPES)[number];
type InsightTileType = (typeof INSIGHT_TILE_TYPES)[number];
type InsightBoardSourceFlow = 'Homescreen' | 'Worksheet';
type InsightDrillCreationPath = 'Manual' | 'Suggested';
export type InsightTileCreationPath = 'All Suggestions' | 'Guided Configuration' | 'Manual' | 'Selected Suggestions';
type InsightSourceSelectionPath = 'Recent' | 'Search';
export type InsightEditProperty =
  | 'Axis Labels'
  | 'Chart Type'
  | 'Color'
  | 'Gauge Bands'
  | 'KPI Precision'
  | 'KPI Threshold'
  | 'KPI Unit'
  | 'Map Tooltip Labels'
  | 'Number Formatting'
  | 'Series Delete'
  | 'Series Labels'
  | 'Series Rename'
  | 'Table Columns'
  | 'Table Title Visibility'
  | 'Tile Background'
  | 'Title and Subtitle';

type InsightBoardEvent =
  | { category: typeof INSIGHT_TELEMETRY_BOARD; action: 'Create Clicked'; sourceFlow: InsightBoardSourceFlow }
  | { category: typeof INSIGHT_TELEMETRY_BOARD; action: 'Delete Clicked' | 'Duplicate Clicked' }
  | { category: typeof INSIGHT_TELEMETRY_BOARD; action: 'Guided Flow Started' }
  | {
      category: typeof INSIGHT_TELEMETRY_BOARD;
      action: 'Opened';
      darkMode: boolean;
      mode: string | undefined;
      workbookId: string;
      worksheetId: string;
    };

type InsightTileEvent =
  | {
      category: typeof INSIGHT_TELEMETRY_TILE;
      action: 'Create Clicked';
      chartTypes?: string[];
      creationPath?: InsightTileCreationPath;
      source?: 'Add Button' | 'Suggested Next Step';
      tileCount: number;
      tileType: InsightTileType;
    }
  | {
      category: typeof INSIGHT_TELEMETRY_TILE;
      action: 'Created';
      chartType?: InsightChartType;
      tileType: InsightTileType;
    }
  | { category: typeof INSIGHT_TELEMETRY_TILE; action: 'Chart Type Selected'; chartType: string }
  | {
      category: typeof INSIGHT_TELEMETRY_TILE;
      action: 'Column Added' | 'Column Removed';
      tileType: 'CHART';
    }
  | {
      category: typeof INSIGHT_TELEMETRY_TILE;
      action: 'Continue Clicked';
      selectedColumnCount: number;
      tileType: 'CHART';
    }
  | { category: typeof INSIGHT_TELEMETRY_TILE; action: 'Delete Clicked' | 'Details Opened'; tileType: InsightTileType }
  | { category: typeof INSIGHT_TELEMETRY_TILE; action: 'Navigated to Source' | 'Source Search Opened' }
  | {
      category: typeof INSIGHT_TELEMETRY_TILE;
      action: 'Source Selected';
      selectionPath: InsightSourceSelectionPath;
    }
  | { category: typeof INSIGHT_TELEMETRY_TILE; action: 'Suggestions Requested' };

type InsightDrillingEvent =
  | {
      category: typeof INSIGHT_TELEMETRY_DRILLING;
      action:
        | 'Breadcrumb Clicked'
        | 'Cancel Clicked'
        | 'Delete Clicked'
        | 'Disable Clicked'
        | 'Drilled Down'
        | 'Edit Clicked'
        | 'Enable Clicked'
        | 'Manage Clicked'
        | 'Override Level Name Clicked'
        | 'Rolled Up'
        | 'Suggestions Requested'
        | 'Update Clicked';
    }
  | {
      category: typeof INSIGHT_TELEMETRY_DRILLING;
      action: 'Create Clicked';
      creationPath: InsightDrillCreationPath;
    };

type InsightFilteringEvent =
  | { category: typeof INSIGHT_TELEMETRY_FILTERING; action: 'Create Clicked' | 'Created' }
  | {
      category: typeof INSIGHT_TELEMETRY_FILTERING;
      action: 'Value Changed';
      filterType: 'SELECT';
      selectedValueCount: number;
    }
  | {
      category: typeof INSIGHT_TELEMETRY_FILTERING;
      action: 'Value Changed';
      activeBoundCount: number;
      filterType: 'RANGE';
    }
  | { category: typeof INSIGHT_TELEMETRY_FILTERING; action: 'Clear All'; filterCount: number };

type InsightAiInsightEvent = { category: typeof INSIGHT_TELEMETRY_AI_INSIGHT; action: 'Date Changed' };

type InsightEditingEvent = {
  category: typeof INSIGHT_TELEMETRY_EDITING;
  action: 'Update Clicked';
  editProperty: InsightEditProperty;
  tileType?: InsightTileType;
};

export type InsightEvent =
  | InsightAiInsightEvent
  | InsightBoardEvent
  | InsightDrillingEvent
  | InsightEditingEvent
  | InsightFilteringEvent
  | InsightTileEvent;

type InsightInformation = Record<string, boolean | number | string | string[] | undefined>;

export function trackInsight(event: InsightEvent): void {
  const information: InsightInformation = { category: event.category };

  if (event.category === INSIGHT_TELEMETRY_TILE) {
    if ('tileType' in event && event.tileType) information.tileType = event.tileType;
    if (event.action === 'Create Clicked') {
      if (event.chartTypes) information.chartTypes = event.chartTypes;
      if (event.creationPath) information.creationPath = event.creationPath;
      information.tileCount = event.tileCount;
      if (event.source) information.source = event.source;
    }
    if (event.action === 'Created' && event.chartType) information.chartType = event.chartType;
    if (event.action === 'Chart Type Selected') information.chartType = event.chartType;
    if (event.action === 'Continue Clicked') information.selectedColumnCount = event.selectedColumnCount;
    if (event.action === 'Source Selected') information.selectionPath = event.selectionPath;
  } else if (event.category === INSIGHT_TELEMETRY_BOARD) {
    if (event.action === 'Create Clicked') information.sourceFlow = event.sourceFlow;
    if (event.action === 'Opened') {
      information.mode = event.mode;
      information.workbookId = event.workbookId;
      information.worksheetId = event.worksheetId;
      information.darkMode = event.darkMode;
    }
  } else if (event.category === INSIGHT_TELEMETRY_DRILLING && event.action === 'Create Clicked') {
    information.creationPath = event.creationPath;
  } else if (event.category === INSIGHT_TELEMETRY_EDITING) {
    information.editProperty = event.editProperty;
    if (event.tileType) information.tileType = event.tileType;
  } else if (event.category === INSIGHT_TELEMETRY_FILTERING) {
    if ('filterType' in event) information.filterType = event.filterType;
    if (event.action === 'Value Changed' && event.filterType === 'SELECT') {
      information.selectedValueCount = event.selectedValueCount;
    }
    if (event.action === 'Value Changed' && event.filterType === 'RANGE') {
      information.activeBoundCount = event.activeBoundCount;
    }
    if (event.action === 'Clear All') information.filterCount = event.filterCount;
  }

  doTrack(INSIGHT_EVENT, event.action, information);
}

export function getInsightTileType(value: string | null | undefined): InsightTileType | undefined {
  return value && isAllowedValue(INSIGHT_TILE_TYPES, value) ? value : undefined;
}

export function getInsightChartType(value: string | null | undefined): InsightChartType | undefined {
  return value && isAllowedValue(INSIGHT_CHART_TYPES, value) ? value : undefined;
}

function isAllowedValue<Value extends string>(allowedValues: readonly Value[], value: string): value is Value {
  return allowedValues.some((allowedValue) => allowedValue === value);
}
